import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import { getCommunity } from 'helpers/community';
import EmpoweredSelector from 'components/EmpoweredSelector';
import FieldSmallComment from 'components/FieldSmallComment';
import { formatNumber } from 'helpers/generic';

const Table = styled.table` 
    font-size: 12px;

    thead {
        th {
            font-weight: 500;

            &:nth-child(1) {
                width: 250px;
            }
        }
    }

    th, td {
        &:nth-child(3),
        &:nth-child(4),
        &:nth-child(5) {
            width: 90px;
            min-width: 90px;
            text-align: right;
        }
    }
`;

let axiosCancelToken = null;

export default function Model111() {
	let [financialYear, setFinancialYear] = useState(moment().format('YYYY'));
	let [quarter, setQuarter] = useState(moment().quarter());
	let [tax, setTax] = useState({
        id: null,
        name: null
    });
    let [community, setCommunity] = useState({
		id: getCommunity()?.id,
		name: getCommunity()?.name
	});
    let [data, setData] = useState([]);

    useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();
	
		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

    useEffect(() => {
        const getData = async () => {     
            axiosCancelToken.cancel(); // Cancel previous request
            axiosCancelToken = axios.CancelToken.source(); // Get new token

            await axios.get('/api/manager/administration-models/get-111', {
                params: {
                    community_id: community?.id,
                    financial_year: financialYear,
                    quarter: quarter,
                    tax_id: tax?.id
                },
                cancelToken: axiosCancelToken.token
            }).then((response) => {
                setData(response.data);
            }).catch((error) => {
                if ( axios.isCancel(error) ) return;
            });	
        }
        getData();
    }, [financialYear, community?.id, quarter, tax?.id]);

    const loadFinancialYears = (input, callback, args) => {
		axios.get('/api/manager/administration-models/financialyears-list', {
			params: {
                community_id: args.community_id
			},
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let formatted = response.data.map((el) => {
				return {
					value: el, 
					label: el
				};
			});
			callback(formatted);
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});	
	}

    const loadCommunities = (input, callback) => {
		axios.get('/api/manager/communities/list', {
			params: {
				search: input,
				no_paginate: true,
			},
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let formatted = response.data.map((el) => {
				return {
					value: el, 
					label: el.name
				};
			});
			callback(formatted);
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});	
	}

    const loadTaxes = (input, callback) => {
		axios.get('/api/manager/taxes/list', {
			params: {
				search: input,
				no_paginate: true,
			},
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let formatted = response.data.map((el) => {
				return {
					value: el, 
					label: el.name
				};
			});
			callback(formatted);
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});	
	}

    const exportModel = async () => {
        await axios.post('api/manager/administration-models/export/111', {
            community_id: community?.id,
            financial_year: financialYear,
            quarter: quarter,
            tax_id: tax?.id
        }, {
			responseType: 'blob',
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'modelo111.xlsx');
			document.body.appendChild(link);
			link.click();
			link.parentElement.removeChild(link);
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});
    }

	return (
		<React.Fragment>
            <div className="row">
                <div className="col-md-3">
                    <label>Comunidad</label>
                    <EmpoweredSelector
                        load={loadCommunities}
                        onChange={(value) => setCommunity({id: value?.id, name: value?.name})}
                        timeout={250}
                        label={community?.name ?? ''}
                        placeholder="Todas las comunidades"
                        value={community?.id}
                    />
                </div>
                <div className="col-md-2">
                    <label>Año fiscal</label>
                    <EmpoweredSelector
                        load={loadFinancialYears}
                        args={{community_id: community?.id}}
                        onChange={(value) => setFinancialYear(value)}
                        timeout={250}
                        label={financialYear ?? ''}
                        placeholder=""
                        value={financialYear ?? ''}
                        noSearcheable={true}
                    />
                </div>
                <div className="col-md-2">
                    <label>Trimestre</label>
                    <select className="form-control form-control-sm" value={quarter} onChange={(e) => setQuarter(e.target.value)}>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                    </select>
                </div>
                <div className="col-md-3">
                    <label>Impuesto</label>
                    <EmpoweredSelector
                        load={loadTaxes}
                        onChange={(value) => setTax(value)}
                        timeout={250}
                        label={tax?.name ?? ''}
                        placeholder=""
                        value={tax?.id ?? ''}
                    />
                    <FieldSmallComment className="mt-1">
                        <i className="bi bi-info-circle-fill text-primary"></i> Debes seleccionar el impuesto de retención del que quieras obtener el informe
                    </FieldSmallComment>
                </div>
                <div className="col-md-2 text-end pt-4">
                    <button className="btn btn-sm btn-light" onClick={exportModel}>Exportar</button>
                </div>
            </div>

            <div className="table-responsive mt-3">
                <Table className="table table-sm table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>Comunidad</th>
                            <th>Proveedor</th>
                            <th>Base</th>
                            <th>Retención</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((el, idx) => {
                            return (
                                <tr key={idx}>
                                    <td>{el.community?.name}</td>
                                    <td>
                                        {el.provider?.business_name}
                                        <span className="badge bg-light text-secondary ms-1 me-1">CIF {el.provider?.vatnumber}</span>
                                        <span className="badge bg-light text-secondary">C.P. {el.provider?.postalcode}</span>
                                    </td>
                                    <td>{formatNumber(el.base)} €</td>
                                    <td>{formatNumber(el.tax)} €</td>
                                    <td>{formatNumber(el.total)} €</td>
                                </tr>
                            )
                        })}
                        {!data.length && <tr><td colSpan="100%">No se han encontrado resultados</td></tr>}
                    </tbody>
                </Table>
            </div>  
        </React.Fragment>
	);
}


